import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

const PostPropertyForm = ({ service, onClose }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const [propertyId, setPropertyId] = useState(null);
  const [purchaseId, setPurchaseId] = useState(null);
  const [approved, setApproved] = useState(false); // State to store approved status
  const [property, setProperty] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const { id } = useParams();

  // Toggle dropdown visibility
  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleClick = () => {
    navigate("/lease", { state: { propertyId, purchaseId } });
  };

  // ✅ Pass IDs when navigating to Rental Form
  const rental = () => {
    navigate("/rental", { state: { propertyId, purchaseId } });
  };

  // Close dropdown when clicking outside of it
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowDropdown(false);
    }
  };

  // Add event listener for click outside dropdown
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ========================= api integration =========================
  // Fetch property details
  useEffect(() => {
    const fetchPurchasedProperties = async () => {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(
          "https://silver-tlc-backend-delta.vercel.app/api/v1/userpurchase/property/get",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: authToken }),
          }
        );

        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

        const data = await response.json();
        const purchasedProperties = data.propertyPurchaseData?.propertypurchase || [];
        setPropertyId(purchasedProperties[0].propertyId._id);
        setPurchaseId(data.propertyPurchaseData.propertypurchase[0]._id);

        // Find the specific property by ID
        const foundProperty = purchasedProperties.find(
          (purchase) => purchase.propertyId._id === id
        );

        if (foundProperty) {
          setProperty(foundProperty.propertyId);
          setApproved(foundProperty.approved); // Set the approved status
        } else {
          console.error("Property not found");
        }
      } catch (error) {
        console.error("Error fetching purchased properties:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPurchasedProperties();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="spinner-border text-primary" role="status">
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-gray-500">Property not found.</div>
      </div>
    );
  }

  // breadcrumb
  const handleBreadcrumbClick = () => {
    navigate("/Dashboard/services");
  };

  return (
    <div className="flex flex-col p-8 bg-white rounded-lg w-full max-w-11xl mx-auto">
      {/* Header Section */}
      <div className="flex justify-start items-start flex-col mb-6">
        {/* Breadcrumbs */}
        <div className="mb-4 text-sm">
          <button
            onClick={handleBreadcrumbClick}
            className="text-gray-500 hover:text-gray-700"
          >
            My Services
          </button>
          <span className="mx-2 text-gray-400">&gt;</span>
          <span className="text-purple-600 font-medium">Property Form</span>
        </div>
        <h2 className="text-2xl font-semibold">Post a Service or Property</h2>
      </div>

      {/* Property Details Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Left Section */}
        <div className="p-4 border rounded-md space-y-4">
          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">Property Title</h3>
            <p className="text-gray-500">
              {property.propertyDetails?.title || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">Listing ID</h3>
            <p className="text-gray-500">
              {property.propertyDetails?.listingID || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">Address</h3>
            <p className="text-gray-500">
              {property.location?.address || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">Price</h3>
            <p className="text-gray-500">
              {property.propertyDetails?.price || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">Property Type</h3>
            <p className="text-gray-500">
              {property.propertyDetails?.propertyType || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">Status</h3>
            <p className="text-gray-500">
              {property.propertyDetails?.status || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">
              Number of Bedrooms
            </h3>
            <p className="text-gray-500">
              {property.propertyDetails?.numberOfBedrooms || "N/A"}
            </p>
          </div>
        </div>

        {/* Middle Section */}
        <div className="p-4 border rounded-md space-y-4">
          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">
              Number of Bathrooms
            </h3>
            <p className="text-gray-500">
              {property.propertyDetails?.numberOfBathrooms || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">
              Square Footage
            </h3>
            <p className="text-gray-500">
              {property.propertyDetails?.squareFootage || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">Year Built</h3>
            <p className="text-gray-500">
              {property.propertyDetails?.squareFootage || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">Parking</h3>
            <p className="text-gray-500">
              {property.propertyDetails?.yearBuiltOrRemodeled || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">Utilities</h3>
            <p className="text-gray-500">
              {property.propertyDetails?.utilities || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">Appliances</h3>
            <p className="text-gray-500">
              {property.propertyDetails?.Appliances || "N/A"}
            </p>
          </div>
        </div>

        {/* Right Section */}
        <div className="p-4 border rounded-md space-y-4">
          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">Outdoor Areas</h3>
            <p className="text-gray-500">
              {property.propertyDetails?.outdoorAreas || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">Amenities</h3>
            <p className="text-gray-500">
              {property.propertyDetails?.amenities || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">
              Wheelchair Ramp
            </h3>
            <p className="text-gray-500">
              {property.propertyDetails?.wheelchairRamp || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">Managed By</h3>
            <p className="text-gray-500">
              {property.location?.managedBy || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">Pets Allowed</h3>
            <p className="text-gray-500">
              {property.neighborhood?.petsAllowed || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">
              Smoking Allowed
            </h3>
            <p className="text-gray-500">
              {property.neighborhood?.smokingAllowed || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">
              Neighborhood Facts
            </h3>
            <p className="text-gray-500">
              {property.neighborhood?.facts || "N/A"}
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Section for Unit Info */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-4 border rounded-md">
          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">Unit Title</h3>
            <p className="text-gray-500">
              {property.location?.unitTitle || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">City</h3>
            <p className="text-gray-500">{property.location?.city || "N/A"}</p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">State</h3>
            <p className="text-gray-500">{property.location?.state || "N/A"}</p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">ZIP Code</h3>
            <p className="text-gray-500">{property.location?.zip || "N/A"}</p>
          </div>
        </div>

        <div className="p-4 border rounded-md">
          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">Bedrooms</h3>
            <p className="text-gray-500">
              {property.location?.bedrooms || "N/A"}
            </p>
          </div>

          <div className="flex items-center justify-between border-b pb-2">
            <h3 className="text-lg font-medium text-gray-700">Baths</h3>
            <p className="text-gray-500">{property.location?.baths || "N/A"}</p>
          </div>
        </div>
      </div>

      {/* Property Pictures/Videos Section */}
      <div className="mb-8">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Property Pictures/Videos
        </label>
        <div className="flex space-x-4 w-[300px] h-[300px]">
          {property.uploadFiles?.map((imageUrl, index) => (
            <div key={index} className="flex-1 aspect-square">
              <img
                src={imageUrl}
                alt={`Property ${index + 1}`}
                className="w-full h-full object-cover rounded-md"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Property Action Dropdown */}
      <div className="relative flex items-end justify-end">
        {/* Action button always visible, toggles dropdown on click */}
        <button
          onClick={toggleDropdown}
          className="p-2 mt-8 bg-violet-700 text-white font-semibold rounded-md"
        >
          Action
        </button>

        {/* Dropdown content shows when toggled */}
        {showDropdown && (
  <div
    ref={dropdownRef}
    className="absolute w-[20vw] mt-2 bg-white shadow-lg rounded-md z-10"
  >
    {approved ? (
      <>
        {/* Show Lease if approved is true */}
        <Link to={`/dashboard/lease?propertyId=${propertyId}&purchaseId=${purchaseId}`}>
          <button
            onClick={handleClick}
            className="w-full p-2 text-left hover:bg-gray-200"
          >
            Lease
          </button>
        </Link>

        {/* Show Rental Approved if approved is true */}
        <Link to={`/dashboard/rentalapproved?propertyId=${propertyId}&purchaseId=${purchaseId}`}>
          <button
            onClick={rental}
            className="w-full p-2 text-left hover:bg-gray-200"
          >
            Rental Approved
          </button>
        </Link>
      </>
    ) : (
      /* Show only Rental if approved is false */
      <Link to={`/dashboard/rental?propertyId=${propertyId}&purchaseId=${purchaseId}`}>
        <button
          onClick={rental}
          className="w-full p-2 text-left hover:bg-gray-200"
        >
          Rental
        </button>
      </Link>
    )}
  </div>
)}

      </div>
    </div>
  );
};

export default PostPropertyForm;